import type {
  Portfolio,
  PortfolioCreateRequest,
  PortfolioInstrumentsRequest,
  PortfolioListResponse,
  PortfolioUpdateRequest,
} from '@akem/shared';
import axios from './axiosConfig.js';
import { getApiEndpoint } from '../config/api.js';

const BASE_URL = `${getApiEndpoint()}/user/portfolios`;

interface PortfolioResponse {
  portfolio: Portfolio;
}

interface OkResponse {
  ok: boolean;
}

export const portfolioService = {
  async list(): Promise<Portfolio[]> {
    const { data } = await axios.get<PortfolioListResponse>(BASE_URL);
    return data.portfolios ?? [];
  },

  async create(payload: PortfolioCreateRequest): Promise<Portfolio> {
    const { data } = await axios.post<PortfolioResponse>(BASE_URL, payload);
    return data.portfolio;
  },

  async update(
    portfolioId: string,
    payload: PortfolioUpdateRequest
  ): Promise<Portfolio> {
    const { data } = await axios.patch<PortfolioResponse>(
      `${BASE_URL}/${encodeURIComponent(portfolioId)}`,
      payload
    );
    return data.portfolio;
  },

  async remove(portfolioId: string, version?: number): Promise<void> {
    const params = version !== undefined ? { params: { version } } : undefined;
    await axios.delete<OkResponse>(
      `${BASE_URL}/${encodeURIComponent(portfolioId)}`,
      params
    );
  },

  async setDefault(portfolioId: string, version?: number): Promise<Portfolio> {
    const payload: PortfolioUpdateRequest = {
      setDefault: true,
      expectedVersion: version,
    };
    return this.update(portfolioId, payload);
  },

  async addInstruments(
    portfolioId: string,
    payload: PortfolioInstrumentsRequest
  ): Promise<Portfolio> {
    const { data } = await axios.post<PortfolioResponse>(
      `${BASE_URL}/${encodeURIComponent(portfolioId)}/instruments`,
      payload
    );
    return data.portfolio;
  },

  async removeInstrument(
    portfolioId: string,
    value: string,
    options?: { mode?: 'symbol' | 'key'; expectedVersion?: number }
  ): Promise<Portfolio> {
    const params = new URLSearchParams();
    if (options?.mode) {
      params.set('mode', options.mode);
    }
    if (options?.expectedVersion !== undefined) {
      params.set('version', String(options.expectedVersion));
    }

    const { data } = await axios.delete<PortfolioResponse>(
      `${BASE_URL}/${encodeURIComponent(portfolioId)}/instruments/${encodeURIComponent(value)}` +
        (params.size > 0 ? `?${params.toString()}` : '')
    );
    return data.portfolio;
  },
};
